import { SimpleGrid, Stack, Text } from '@mantine/core';
import TagManager from 'react-gtm-module';
import { useTheme } from '@/assets/styles/themeMantine';
import { formatWildfireCurrency } from '@/helpers/wildfire';
import { GoogleEvents } from '@/features/googleEvents';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { SectionHeader } from './SectionHeader';

interface RiskMetricsSectionProps {
  wildfireRisk?: number | null;
  wildfireBurnability?: number | null;
  wildfireExposure?: number | null;
}

const PLACEHOLDER = '—';

const formatCompactUsd = (value?: number | null): string =>
  value == null ? PLACEHOLDER : formatWildfireCurrency(value);

const formatPercent = (value?: number | null): string =>
  value == null ? PLACEHOLDER : `${(value * 100).toFixed(3)}%`;

interface BigNumberProps {
  value: string;
  caption: string;
  testId: string;
}

const BigNumber = ({ value, caption, testId }: BigNumberProps) => {
  const theme = useTheme();
  return (
    <Stack gap={2} data-testid={testId}>
      <Text style={theme.other.typography.variants.h3}>{value}</Text>
      <Text
        style={theme.other.typography.variants.caption}
        c={theme.other.mainColor.foreground.secondary}
        data-testid={`${testId}-caption`}
      >
        {caption}
      </Text>
    </Stack>
  );
};

/**
 * Three top-line risk numbers for the network-group risk summary, laid
 * out as the BigNumber pattern from the design: a prominent total above
 * a two-up row of supporting metrics.
 */
export const RiskMetricsSection = ({
  wildfireRisk,
  wildfireBurnability,
  wildfireExposure,
}: RiskMetricsSectionProps) => {
  const { formatMessage } = useClientTerminology();
  return (
    <Stack gap="md" data-testid="risk-metrics-section" pt="md">
      <SectionHeader
        title={formatMessage({ id: 'groupSummary.riskMetrics.title' })}
        help={{
          text: formatMessage({ id: 'groupSummary.riskMetrics.helpText' }),
          onOpen: () =>
            TagManager.dataLayer({
              dataLayer: { event: GoogleEvents.riskMetricsHelpClicked },
            }),
        }}
      />
      <BigNumber
        value={formatCompactUsd(wildfireRisk)}
        caption={formatMessage({
          id: 'groupSummary.riskMetrics.riskPerYear',
        })}
        testId="risk-metrics-section-wildfire-risk"
      />
      <SimpleGrid cols={2} spacing="md">
        <BigNumber
          value={formatPercent(wildfireBurnability)}
          caption={formatMessage({
            id: 'groupSummary.riskMetrics.burnabilityPerYear',
          })}
          testId="risk-metrics-section-wildfire-burnability"
        />
        <BigNumber
          value={formatCompactUsd(wildfireExposure)}
          caption={formatMessage({
            id: 'groupSummary.riskMetrics.valueWithinRadius',
          })}
          testId="risk-metrics-section-wildfire-exposure"
        />
      </SimpleGrid>
    </Stack>
  );
};
